// LoginChallenge — bước HAI của đăng nhập khi máy chủ trả 403 kèm `error.details.challenge_token`:
//   mfa_required              → nhập mã 6 số (hoặc mã khôi phục)
//   mfa_enrollment_required   → workspace bắt buộc xác thực 2 lớp mà chưa thiết lập: quét QR → mã đầu tiên → lưu mã khôi phục
//   password_change_required  → mật khẩu tạm / không còn đạt chính sách: đặt mật khẩu mới
// Một bước xong có thể dẫn sang bước kế (vd thiết lập MFA xong → đổi mật khẩu): host cập nhật `pending`, component
// tự đổi màn.
//
// Host (HOST_CONTRACT.md · `useAuth().loginChallenge`) giữ trạng thái + gọi API:
//   { pending, cancel(), verifyMfa({code|recovery_code}), startEnroll(), confirmEnroll(code),
//     completePasswordChange(newPassword), complete(result) → user }
// Host KHÔNG có `loginChallenge` (Core) thì component này không bao giờ được dựng.

import { useEffect, useState } from 'react';
import { Loader2, ShieldCheck, ArrowLeft, Lock, Eye, EyeOff } from 'lucide-react';
import { Trans, useLingui } from '@lingui/react/macro';
import { OtpCodeInput, MfaQrSetup, RecoveryCodesPanel, apiErrorText } from './MfaParts.jsx';

/** Lỗi này có phải «cần thêm một bước» không (host đã tự chuyển `pending` sang bước kế). */
export function isLoginChallengeError(err) {
  return !!err?.response?.data?.error?.details?.challenge_token;
}

/** @param {object} props.lc  `useAuth().loginChallenge` của host. @param {function} props.onSuccess (user) => void */
export default function LoginChallenge({ lc, onSuccess }) {
  const { t } = useLingui();
  const pending = lc?.pending;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Kết quả đăng nhập đã có nhưng còn phải cho người dùng lưu mã khôi phục trước khi vào app.
  const [doneResult, setDoneResult] = useState(null);
  const [codesToSave, setCodesToSave] = useState(null);

  useEffect(() => { setError(''); }, [pending?.details?.challenge_token]);

  const finish = async (result) => {
    const user = await lc.complete(result);
    onSuccess?.(user);
  };

  // Bước xong trả lỗi «còn bước nữa» ⇒ host đã đổi pending; chỉ còn phải giữ mã khôi phục (nếu có) để hiện.
  const run = async (fn) => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      const result = await fn();
      if (result?.recovery_codes?.length) { setDoneResult(result); setCodesToSave(result.recovery_codes); return; }
      await finish(result);
    } catch (err) {
      if (isLoginChallengeError(err)) {
        const codes = err.response.data.error.details.recovery_codes;
        if (codes?.length) setCodesToSave(codes);
        return;
      }
      setError(apiErrorText(err, t`Không thực hiện được, vui lòng thử lại`));
    } finally { setBusy(false); }
  };

  if (codesToSave) {
    return (
      <Frame title={t`Lưu mã khôi phục`} onBack={null}>
        <RecoveryCodesPanel
          codes={codesToSave}
          onDone={async () => {
            setCodesToSave(null);
            if (doneResult) { const r = doneResult; setDoneResult(null); await finish(r); }
          }}
        />
      </Frame>
    );
  }

  if (!pending) return null;
  const back = () => lc.cancel();
  const purpose = pending.details?.purpose;

  if (purpose === 'mfa') {
    return <VerifyStep pending={pending} busy={busy} error={error} onBack={back}
      onSubmit={(body) => run(() => lc.verifyMfa(body))} />;
  }
  if (purpose === 'mfa_enroll') {
    return <EnrollStep pending={pending} busy={busy} error={error} onBack={back} setError={setError}
      lc={lc} onConfirm={(code) => run(() => lc.confirmEnroll(code))} />;
  }
  if (purpose === 'pwd_change') {
    return <PasswordStep pending={pending} busy={busy} error={error} onBack={back}
      onSubmit={(pwd) => run(() => lc.completePasswordChange(pwd))} />;
  }
  return null;
}

function Frame({ title, hint, onBack, children }) {
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2">
        <ShieldCheck size={20} className="text-blue-600 shrink-0 mt-0.5" />
        <div>
          <div className="font-semibold text-slate-900">{title}</div>
          {hint && <div className="text-sm text-slate-600 mt-0.5">{hint}</div>}
        </div>
      </div>
      {children}
      {onBack && (
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700">
          <ArrowLeft size={14} /> <Trans>Quay lại đăng nhập</Trans>
        </button>
      )}
    </div>
  );
}

function VerifyStep({ pending, busy, error, onBack, onSubmit }) {
  const { t } = useLingui();
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [recovery, setRecovery] = useState('');
  return (
    <Frame title={t`Xác thực 2 lớp`} hint={pending.message} onBack={onBack}>
      <form
        onSubmit={(e) => { e.preventDefault(); onSubmit(useRecovery ? { recovery_code: recovery.trim() } : { code }); }}
        className="space-y-3"
      >
        {useRecovery ? (
          <input
            type="text" autoFocus value={recovery} onChange={(e) => setRecovery(e.target.value)}
            placeholder={t`Mã khôi phục, vd ABCDE-FGHJK`} autoComplete="off"
            className="w-full font-mono text-center py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
          />
        ) : (
          <OtpCodeInput value={code} onChange={setCode} disabled={busy}
            onComplete={(c) => { if (!busy) onSubmit({ code: c }); }} />
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}
        <button
          type="submit"
          disabled={busy || (useRecovery ? recovery.trim().length < 10 : code.length !== 6)}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2"
        >
          {busy && <Loader2 size={16} className="animate-spin" />}
          <Trans>Xác nhận</Trans>
        </button>
        <button type="button" onClick={() => setUseRecovery((v) => !v)} className="w-full text-sm text-blue-600 hover:underline">
          {useRecovery ? <Trans>Dùng mã 6 số trong ứng dụng</Trans> : <Trans>Mất điện thoại? Dùng mã khôi phục</Trans>}
        </button>
      </form>
    </Frame>
  );
}

function EnrollStep({ pending, busy, error, onBack, lc, onConfirm, setError }) {
  const { t } = useLingui();
  const [setup, setSetup] = useState(null);
  const [loading, setLoading] = useState(false);
  const start = async () => {
    setLoading(true); setError('');
    try { setSetup(await lc.startEnroll()); }
    catch (err) { setError(apiErrorText(err, t`Không tạo được mã QR, vui lòng thử lại`)); }
    finally { setLoading(false); }
  };
  const ws = pending.details?.workspace_name;
  return (
    <Frame
      title={t`Thiết lập xác thực 2 lớp`}
      hint={ws ? t`Workspace «${ws}» bắt buộc xác thực 2 lớp. Thiết lập một lần, các lần đăng nhập sau chỉ cần nhập mã 6 số.` : pending.message}
      onBack={onBack}
    >
      {setup ? (
        <MfaQrSetup setup={setup} onSubmit={onConfirm} submitting={busy} error={error} />
      ) : (
        <>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <button
            type="button" onClick={start} disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            <Trans>Bắt đầu thiết lập</Trans>
          </button>
        </>
      )}
    </Frame>
  );
}

function PasswordStep({ pending, busy, error, onBack, onSubmit }) {
  const { t } = useLingui();
  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const mismatch = confirm && pwd !== confirm;
  return (
    <Frame title={t`Đặt mật khẩu mới`} hint={pending.message} onBack={onBack}>
      <form onSubmit={(e) => { e.preventDefault(); if (!mismatch && pwd) onSubmit(pwd); }} className="space-y-3">
        <p className="text-xs text-slate-500">
          <Trans>Tối thiểu 8 ký tự (tài khoản quản trị: 10). Không dùng mật khẩu quá phổ biến hoặc chứa tên email.</Trans>
        </p>
        <div className="relative">
          <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type={show ? 'text' : 'password'} autoFocus autoComplete="new-password" value={pwd}
            onChange={(e) => setPwd(e.target.value)} placeholder={t`Mật khẩu mới`}
            className="w-full pl-10 pr-10 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm"
          />
          <button type="button" onClick={() => setShow((s) => !s)} aria-label={show ? t`Ẩn mật khẩu` : t`Hiện mật khẩu`}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
            {show ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
        <input
          type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
          placeholder={t`Nhập lại mật khẩu mới`}
          className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm"
        />
        {mismatch && <p className="text-xs text-red-600"><Trans>Mật khẩu không khớp</Trans></p>}
        {error && <p className="text-sm text-red-600">{error}</p>}
        <button
          type="submit" disabled={busy || !pwd || !!mismatch}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2"
        >
          {busy && <Loader2 size={16} className="animate-spin" />}
          <Trans>Lưu mật khẩu và tiếp tục</Trans>
        </button>
      </form>
    </Frame>
  );
}
